import React from 'react';
import ForecastCommentThreadPanel from '../ForecastCommentThreadPanel';
import { MIN_NARRATIVE_WORDS } from '../forecastQualityGates';

const narrativeWordCount = (text) => String(text || '').trim().split(/\s+/).filter(Boolean).length;

const NarrativeWordHint = ({ text }) => {
  const words = narrativeWordCount(text);
  const met = words >= MIN_NARRATIVE_WORDS;
  return (
    <small className="forecast-submit-note" style={{ marginTop: 4, color: met ? undefined : '#b3541e' }}>
      {words} of {MIN_NARRATIVE_WORDS} words minimum{met ? ' ✓' : ''}
    </small>
  );
};

const formatGeneratedAt = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const PROVENANCE_LABELS = {
  'ai-proposed': 'AI proposed',
  'cascade-derived': 'Cascade derived',
  'baseline-carry': 'Baseline carry'
};

const PROVENANCE_TONES = {
  'ai-proposed': 'gold',
  'cascade-derived': 'moss',
  'baseline-carry': 'neutral'
};

/** Presentation-only review view for the regional forecasting workspace. */
const RegionalReviewStage = ({ vm }) => {
  const { Calculator, CheckCircle2, Database, FORECAST_DATA_SOURCE, ForecastAllocation, ForecastChangeDrawer, ForecastValidationList, History, Lock, MODEL_VERSION, RefreshCw, STATUS_LABELS, ShieldCheck, UploadCloud, User, activeUser, activeWorkspaceStage, aggregateUnits, aiSummary, aiSummaryBusy, annualResults, approveLoad, calculationRows, canEditSubmission, commentaryRequired, constraintSummary, expenseInputs, feeDrivers, forecastLoadRows, formatLineageValue, formatMillions, generateAiSummary, hasBlockingRows, hireRows, isAggregate, isLiaison, isTransitioning, isWritingForecast, lineage, lineageHistoryEntries, loadApproved, loadCompleted, loadResult, loadState, macroMode, marketInputs, missingNarratives, narrativeTriggers, narratives, navigateToValidationIssue, recallSubmission, recentLineage, reviewValidationIssues, scenario, scenarioVersion, selectedMarket, setLoadState, setNarratives, shocks, stagedRows, submission, submissionId, submissionStatus, submitToFpa, totalOverrideCount, validationCounts, workforceInputs, workforceYearInputs, workspaceBlockingIssues, writeForecast, yearAdjustments, qualityGates = [], blockingQualityGates = [], qualityOverrides = {}, setQualityOverrides, gateBlockers = [], isFpa = false } = vm;

  // B7 — warning gates (baseline flagged / context warnings) block until an FP&A
  // owner + note override is captured. Errors (duplicate/short narrative) are
  // resolved by editing the narratives, not overridden.
  const warningGates = qualityGates.filter((gate) => gate.severity === 'warning');
  const errorGates = qualityGates.filter((gate) => gate.severity === 'error');
  const updateOverride = (gateCode, field, value) =>
    setQualityOverrides && setQualityOverrides((previous) => ({
      ...previous,
      [gateCode]: { ...(previous[gateCode] || {}), [field]: value }
    }));

  return (
    <>
      <div className="workspace-stage-panel" id="regional-stage-review" tabIndex="-1">
{activeWorkspaceStage === 'review' && isLiaison && submissionId && (
        <section className="forecast-panel forecast-sticky-section" id="forecast-submit">
          <details className="forecast-accordion" open>
            <summary>
              <div>
                <span className="forecast-section-code">OP_08 · Submit to FP&A</span>
                <h2>Validate, explain, and hand the package to central FP&A</h2>
              </div>
              <UploadCloud size={18} />
            </summary>

            <div className="forecast-accordion-body">
              <div className="forecast-subpanel">
                <div className="forecast-panel-head">
                  <div>
                    <span className="forecast-section-code">Submission readiness</span>
                    <h3>Resolve validation issues before handoff</h3>
                  </div>
                  <ShieldCheck size={18} aria-hidden="true" />
                </div>
                <ForecastValidationList
                  issues={reviewValidationIssues}
                  emptyLabel="No blocking validation or commentary issues."
                  onNavigate={navigateToValidationIssue}
                />

                {errorGates.length > 0 && (
                  <div className="forecast-submit-chips" style={{ marginTop: 8 }}>
                    {errorGates.map((gate) => (
                      <span key={gate.code} className="forecast-health-chip red">
                        {gate.message}
                      </span>
                    ))}
                  </div>
                )}

                {warningGates.length > 0 && (
                  <div className="forecast-quality-gates" style={{ marginTop: 10 }}>
                    {warningGates.map((gate) => {
                      const override = qualityOverrides[gate.code] || {};
                      return (
                        <div key={gate.code} className={`forecast-subpanel forecast-quality-gate ${gate.blocking ? 'blocking' : 'overridden'}`}>
                          <div className="forecast-submit-chips">
                            <span className={`forecast-health-chip ${gate.blocking ? 'red' : 'moss'}`}>
                              {gate.blocking ? 'Override required' : 'Overridden'}
                            </span>
                            <span>{gate.message}</span>
                          </div>
                          {isFpa ? (
                            <div className="forecast-quality-gate-inputs">
                              <label className="forecast-submit-narrative">
                                <span>Override owner</span>
                                <input
                                  type="text"
                                  value={override.owner || ''}
                                  disabled={!canEditSubmission}
                                  placeholder="Accountable FP&A owner"
                                  onChange={(event) => updateOverride(gate.code, 'owner', event.target.value)}
                                />
                              </label>
                              <label className="forecast-submit-narrative">
                                <span>Override note</span>
                                <textarea
                                  rows={2}
                                  value={override.note || ''}
                                  disabled={!canEditSubmission}
                                  placeholder="Why it is acceptable to proceed despite this warning…"
                                  onChange={(event) => updateOverride(gate.code, 'note', event.target.value)}
                                />
                              </label>
                            </div>
                          ) : (
                            <p className="forecast-submit-note">
                              Only FP&A can override this warning.
                              {gate.code === 'baseline_flagged' && (
                                <>
                                  {' '}To submit without an override, return to the Baseline stage and
                                  confirm the baseline (this withdraws the flag).{' '}
                                  <button
                                    type="button"
                                    className="forecast-secondary-button compact"
                                    onClick={() => navigateToValidationIssue?.({ stage: 'baseline', targetId: 'baseline-review-decision' })}
                                  >
                                    Go to baseline decision
                                  </button>
                                </>
                              )}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {gateBlockers.length > 0 && (
                  <div className="forecast-submit-chips" style={{ marginTop: 8 }}>
                    <span className="forecast-health-chip red">Server rejected submission</span>
                    {gateBlockers.map((gate) => (
                      <span key={gate.code} className="forecast-health-chip red">{gate.message}</span>
                    ))}
                  </div>
                )}
              </div>

              <label className="forecast-submit-narrative" htmlFor="forecast-narrative-submission-summary">
                <span>
                  Package commentary
                  {commentaryRequired ? ` (required · at least ${MIN_NARRATIVE_WORDS} words)` : ' (optional)'}
                </span>
                <textarea
                  id="forecast-narrative-submission-summary"
                  rows={4}
                  value={narratives['submission-summary'] || ''}
                  disabled={!canEditSubmission}
                  placeholder="Summarize the decisions made, their expected financial effect, and the key execution risk…"
                  onChange={(event) =>
                    setNarratives((previous) => ({ ...previous, 'submission-summary': event.target.value }))
                  }
                />
                {commentaryRequired && <NarrativeWordHint text={narratives['submission-summary']} />}
              </label>

              <div className="forecast-submit-grid">
                <div className="forecast-submit-block">
                  <h3>Validation</h3>
                  <div className="forecast-submit-chips">
                    <span className={`forecast-health-chip ${hasBlockingRows ? 'red' : 'green'}`}>
                      {hasBlockingRows
                        ? `${stagedRows.filter((row) => row.status === 'Blocked').length} blocked row(s)`
                        : 'No blocked rows'}
                    </span>
                    <span className="forecast-health-chip neutral">{totalOverrideCount} active inputs</span>
                    <span className="forecast-health-chip neutral">{forecastLoadRows.length} staged cube rows</span>
                  </div>
                  <p className="forecast-submit-note">
                    Submitting locks the package for FP&A review. The forecast cube write is performed
                    by FP&A from the review console after approval — market packages never write
                    directly to GL_Fact.
                  </p>
                </div>

                <div className="forecast-submit-block">
                  <h3>Triggered variance narratives {narrativeTriggers.length === 0 && <small>(none required)</small>}</h3>
                  {narrativeTriggers.map((trigger) => (
                    <label className="forecast-submit-narrative" htmlFor={`forecast-narrative-${trigger.id}`} key={trigger.id}>
                      <span>{trigger.label} <small>(at least {MIN_NARRATIVE_WORDS} words)</small></span>
                      <textarea
                        id={`forecast-narrative-${trigger.id}`}
                        rows={3}
                        value={narratives[trigger.id] || ''}
                        disabled={!canEditSubmission}
                        placeholder="Explain the driver, the local evidence, and the risk if you are wrong…"
                        onChange={(event) =>
                          setNarratives((previous) => ({ ...previous, [trigger.id]: event.target.value }))
                        }
                      />
                      <NarrativeWordHint text={narratives[trigger.id]} />
                    </label>
                  ))}
                </div>
              </div>

              <div className="forecast-subpanel forecast-ai-summary">
                <div className="forecast-panel-head">
                  <span className="forecast-section-code">OP_08AI · Change summary</span>
                  <h3>Auto-generated read of what this package changes — travels with the submission to FP&A and the executive view</h3>
                </div>
                {aiSummary ? (
                  <blockquote className="forecast-ai-quote">
                    <strong>{aiSummary.headline}</strong>
                    {(aiSummary.drivers || []).length > 0 && (
                      <p>{(aiSummary.drivers || []).join(' · ')}</p>
                    )}
                    {aiSummary.risk && <p className="forecast-ai-risk">Risk: {aiSummary.risk}</p>}
                    <small>
                      {aiSummary.generator === 'haiku' ? 'Written by claude-haiku from the package diff' : 'Rule-generated from the package diff'}
                      {aiSummary.generatedAt ? ` · ${formatGeneratedAt(aiSummary.generatedAt)}` : ''}
                    </small>
                  </blockquote>
                ) : (
                  <p className="forecast-submit-note">Generated automatically at submit, or on demand below.</p>
                )}
                <div className="forecast-submit-chips">
                  <button
                    type="button"
                    className="forecast-secondary-button compact"
                    disabled={aiSummaryBusy || !submissionId}
                    onClick={generateAiSummary}
                  >
                    {aiSummaryBusy ? 'Generating…' : aiSummary ? 'Regenerate draft' : 'Generate draft'}
                  </button>
                  {aiSummary && aiSummary.generator !== 'error' && canEditSubmission && (
                    <button
                      type="button"
                      className="forecast-primary-button compact"
                      onClick={() => {
                        const composed = [
                          aiSummary.headline,
                          (aiSummary.drivers || []).length ? `Drivers: ${(aiSummary.drivers || []).join('; ')}.` : '',
                          aiSummary.risk ? `Risk: ${aiSummary.risk}` : ''
                        ].filter(Boolean).join(' ');
                        setNarratives((previous) => {
                          const next = { ...previous };
                          if (!(next['submission-summary'] || '').trim()) {
                            next['submission-summary'] = composed;
                          } else {
                            next['submission-summary'] = `${next['submission-summary']}\n\n${composed}`;
                          }
                          return next;
                        });
                      }}
                    >
                      Apply draft to package commentary
                    </button>
                  )}
                </div>
              </div>

              <ForecastChangeDrawer title="Assumption change history" entries={lineageHistoryEntries} />

              {canEditSubmission
                && (hasBlockingRows || workspaceBlockingIssues.length > 0
                  || missingNarratives.length > 0 || blockingQualityGates.length > 0) && (
                <div className="forecast-submit-chips" style={{ marginTop: 10 }}>
                  <span className="forecast-health-chip red">Before you can submit</span>
                  {workspaceBlockingIssues.map((issue) => (
                    <button
                      key={issue.id}
                      type="button"
                      className="forecast-health-chip red clickable"
                      onClick={() => navigateToValidationIssue?.(issue)}
                    >
                      {issue.label}
                    </button>
                  ))}
                  {missingNarratives.map((trigger) => (
                    <button
                      key={trigger.id}
                      type="button"
                      className="forecast-health-chip red clickable"
                      onClick={() => document.getElementById(`forecast-narrative-${trigger.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                    >
                      {trigger.id === 'submission-summary' ? 'Package commentary missing' : trigger.label}
                    </button>
                  ))}
                  {blockingQualityGates.map((gate) => (
                    <button
                      key={gate.code}
                      type="button"
                      className="forecast-health-chip red clickable"
                      onClick={() => {
                        const target = gate.triggers?.length
                          ? document.getElementById(`forecast-narrative-${gate.triggers[0]}`)
                          : document.getElementById('forecast-submit');
                        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                    >
                      {gate.message}
                    </button>
                  ))}
                  {hasBlockingRows && (
                    <span className="forecast-health-chip red">Blocked staged rows — see validation above</span>
                  )}
                </div>
              )}

              <div className="forecast-submit-actions">
                {canEditSubmission ? (
                  <button
                    type="button"
                    className="forecast-primary-button"
                    disabled={hasBlockingRows || workspaceBlockingIssues.length > 0 || missingNarratives.length > 0 || blockingQualityGates.length > 0 || isTransitioning}
                    onClick={submitToFpa}
                  >
                    <UploadCloud size={15} />
                    {isTransitioning
                      ? 'Submitting…'
                      : workspaceBlockingIssues.length > 0
                      ? `Review required (${workspaceBlockingIssues.length})`
                      : blockingQualityGates.length > 0
                      ? `Quality gates (${blockingQualityGates.length})`
                      : missingNarratives.length > 0
                      ? `Narratives required (${missingNarratives.length})`
                      : hasBlockingRows
                      ? 'Blocked rows prevent submit'
                      : 'Submit to FP&A'}
                  </button>
                ) : submissionStatus === 'submitted' ? (
                  <>
                    <span className="forecast-health-chip gold">Submitted — locked for review</span>
                    {!submission?.review_opened_at && (
                      <button
                        type="button"
                        className="forecast-secondary-button"
                        disabled={isTransitioning}
                        onClick={recallSubmission}
                      >
                        Recall submission
                      </button>
                    )}
                  </>
                ) : (
                  <span className="forecast-health-chip moss">
                    {STATUS_LABELS[submissionStatus] || 'Locked'}
                  </span>
                )}
              </div>
            </div>
          </details>
        </section>
      )}

{activeWorkspaceStage === 'review' && isAggregate && aggregateUnits.length > 0 && (
        <ForecastAllocation
          units={aggregateUnits}
          annualResults={annualResults}
          yearAdjustments={yearAdjustments}
          workforceYearInputs={workforceYearInputs}
          feeDrivers={feeDrivers}
          hireRows={hireRows}
          marketInputs={marketInputs}
          workforceInputs={workforceInputs}
          expenseInputs={expenseInputs}
          shocks={shocks}
          scenario={scenario}
          macroMode={macroMode}
          lineage={lineage}
          constraintSummary={constraintSummary}
          scenarioVersion={scenarioVersion}
          actor={activeUser}
        />
      )}

{activeWorkspaceStage === 'review' && !isLiaison && !isAggregate && (
      <section className="forecast-panel forecast-sticky-section" id="forecast-load">
        <details className="forecast-accordion" open>
          <summary>
            <div>
              <span className="forecast-section-code">OP_08 · Forecast load center</span>
              <h2>Stage, validate, approve, write, reconcile</h2>
            </div>
            <Lock size={18} />
          </summary>

          <div className="forecast-accordion-body">

        <div className="forecast-load-rail">
          <div className={loadState === 'validated' ? 'active' : 'done'}>
            <CheckCircle2 size={16} />
            Stage + validate
          </div>
          <div className={loadApproved ? 'done' : 'active'}>
            <ShieldCheck size={16} />
            Approve
          </div>
          <div className={loadCompleted ? 'done' : loadApproved ? 'active' : ''}>
            <UploadCloud size={16} />
            Write to cube
          </div>
          <div className={loadCompleted ? 'done' : ''}>
            <Database size={16} />
            Reconcile + lock
          </div>
        </div>

        <div className="forecast-load-summary">
          <div>
            <span>Control rows</span>
            <strong>{stagedRows.length}</strong>
          </div>
          <div>
            <span>Cube rows</span>
            <strong>{forecastLoadRows.length}</strong>
          </div>
          <div>
            <span>Validated</span>
            <strong>{validationCounts.Validated || 0}</strong>
          </div>
          <div>
            <span>Warnings</span>
            <strong>{validationCounts.Warning || 0}</strong>
          </div>
          <div>
            <span>Blocked</span>
            <strong>{validationCounts.Blocked || 0}</strong>
          </div>
          <div>
            <span>Load batch</span>
            <strong>{loadResult?.loadBatchId || 'Draft'}</strong>
          </div>
          <div>
            <span>Prior rows replaced</span>
            <strong>{loadResult?.replacedRows ?? 0}</strong>
          </div>
        </div>

        <div className={`forecast-load-message ${loadResult?.status === 'error' ? 'error' : loadCompleted ? 'success' : ''}`}>
          <Database size={17} />
          <div>
            <strong>{loadResult?.status === 'error' ? 'Supabase write failed' : 'Supabase GL_Fact target'}</strong>
            <small>
              {loadResult?.status === 'error'
                ? loadResult.message
                : `${FORECAST_DATA_SOURCE} / ${selectedMarket.planningEntity} / ${scenarioVersion}`}
            </small>
          </div>
        </div>

        <div className="forecast-table-shell ace-scroll-shell">
          <table className="forecast-table">
            <thead>
              <tr>
                <th>GL account</th>
                <th>Line item</th>
                <th>Source module</th>
                <th>Period</th>
                <th>Financial value</th>
                <th>Provenance</th>
                <th>Validation</th>
              </tr>
            </thead>
            <tbody>
              {stagedRows.map((row) => (
                <tr key={`${row.gl}-${row.line}`}>
                  <td>{row.gl}</td>
                  <td>
                    <strong>{row.line}</strong>
                    <small>{selectedMarket.planningEntity} / {scenarioVersion}</small>
                  </td>
                  <td>{row.module}</td>
                  <td>{row.period}</td>
                  <td>{formatMillions(row.value)}</td>
                  <td>
                    <span className={`forecast-health-chip ${PROVENANCE_TONES[row.forecastProvenance] || 'neutral'} mini`}>
                      {PROVENANCE_LABELS[row.forecastProvenance] || PROVENANCE_LABELS['baseline-carry']}
                    </span>
                  </td>
                  <td>
                    <span className={`forecast-validation ${row.status.toLowerCase()}`}>
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="forecast-actions">
          <button
            type="button"
            className="forecast-secondary-action"
            onClick={() => setLoadState('validated')}
          >
            <RefreshCw size={15} />
            Revalidate
          </button>
          <button
            type="button"
            className="forecast-primary-action"
            onClick={approveLoad}
            disabled={hasBlockingRows || loadApproved}
          >
            <ShieldCheck size={15} />
            Approve load
          </button>
          <button
            type="button"
            className="forecast-primary-action"
            onClick={writeForecast}
            disabled={!loadApproved || hasBlockingRows || loadCompleted || isWritingForecast}
          >
            <UploadCloud size={15} />
            {isWritingForecast ? 'Writing forecast' : 'Write forecast'}
          </button>
        </div>
          </div>
        </details>
      </section>
      )}

{activeWorkspaceStage === 'review' && <section className="forecast-panel">
        <details className="forecast-accordion">
          <summary>
            <div>
              <span className="forecast-section-code">OP_06 · Assumption lineage</span>
              <h2>Who changed the business-unit forecast and why</h2>
            </div>
            <History size={18} />
          </summary>

          <div className="forecast-accordion-body">
            <div className="forecast-grid forecast-grid-contained">
        <div className="forecast-panel">
          <div className="forecast-panel-head">
            <div>
              <span className="forecast-section-code">OP_06B · Assumption lineage</span>
              <h3>Who inputted which number</h3>
            </div>
            <History size={18} />
          </div>

          <div className="forecast-lineage-list">
            {recentLineage.length === 0 ? (
              <div className="forecast-empty-state">
                <User size={20} />
                <strong>No manual changes yet</strong>
                <span>Edit a hiring, expense, or macro assumption to capture FP&A user lineage.</span>
              </div>
            ) : (
              recentLineage.map((entry) => (
                <div className="forecast-lineage-row" key={`${entry.key}-${entry.timestamp}`}>
                  <span>{entry.field}</span>
                  <strong>{entry.user}</strong>
                  <small>
                    {formatLineageValue(entry.field, entry.priorValue)} to {formatLineageValue(entry.field, entry.newValue)} / {entry.timestamp}
                  </small>
                </div>
              ))
            )}
          </div>

          <div className="forecast-data-contract">
            <div>
              <Database size={18} />
              <span>
                Actuals pull: Databricks MCP joins `GL_Fact`, Entity Dimension, and GL Account
                Dimension at Region + Market planning entity grain.
              </span>
            </div>
            <div>
              <UploadCloud size={18} />
              <span>
                Forecast writeback: staged rows land in `GL_Fact` forecast scenario with cycle,
                load batch, source module, and lineage IDs.
              </span>
            </div>
            <div>
              <Calculator size={18} />
              <span>
                Model control: all cards, charts, and staged GL rows are derived from
                {` ${MODEL_VERSION}`} using $MM financial units.
              </span>
            </div>
          </div>
        </div>
            </div>
          </div>
        </details>
      </section>}

{activeWorkspaceStage === 'review' && <section className="forecast-panel">
        <details className="forecast-accordion">
          <summary>
            <div>
              <span className="forecast-section-code">OP_07 · Model validation</span>
              <h2>Financial calculation path</h2>
            </div>
            <Calculator size={18} />
          </summary>

          <div className="forecast-accordion-body">

        <div className="forecast-audit-grid">
          <div>
            <span>Model version</span>
            <strong>{MODEL_VERSION}</strong>
            <small>All financial values shown in $MM unless noted.</small>
          </div>
          <div>
            <span>P&L equation</span>
            <strong>NII + fees - NIE - provision - tax</strong>
            <small>Same model object feeds review cards, charts, constraints, and staged rows.</small>
          </div>
          <div>
            <span>Rate mechanics</span>
            <strong>SOFR, beta, floor</strong>
            <small>Loan yield and deposit cost reprice from the selected macro set.</small>
          </div>
          <div>
            <span>Expense mechanics</span>
            <strong>Spend delta, not spend double-count</strong>
            <small>Market levers adjust central budget; legal recoupment is a contra-expense.</small>
          </div>
        </div>

        <div className="forecast-table-shell ace-scroll-shell">
          <table className="forecast-table forecast-calculation-table">
            <thead>
              <tr>
                <th>Driver</th>
                <th>Formula path</th>
                <th>Output</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {calculationRows.map((row) => (
                <tr key={row.driver}>
                  <td>
                    <strong>{row.driver}</strong>
                  </td>
                  <td>{row.formula}</td>
                  <td>{row.output}</td>
                  <td>{row.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          </div>
        </details>
      </section>}
      {submissionId && (
        <ForecastCommentThreadPanel submissionId={submissionId} scopeType="package" compact />
      )}
      </div>
    </>
  );
};

export default RegionalReviewStage;
